import { useState, useEffect, useCallback, useMemo } from "react";
import { useControls } from "leva";
import {
  ShadowSystemResult,
  ShadowConfig,
  SceneOpticsConfig,
} from "@/types/laser-engraving";
import {
  DEFAULT_SHADOW_CONFIG,
  SHADOW_RANGES,
} from "@/lib/laser-physics";

/**
 * 基于点云就绪状态的智能阴影系统Hook
 * 根据点云完全就绪状态来控制阴影启用，并提供完整的阴影配置管理
 * 
 * @param sceneOptics - 场景光学配置（包含光源位置和效果平面位置）
 * @param isPointCloudReady - 点云是否完全就绪
 * @param panelName - 控制面板名称
 * @param initialConfig - 初始配置（可选）
 * @returns 阴影系统状态和控制函数
 */
export function useSmartShadowSystem(
  sceneOptics: SceneOpticsConfig,
  isPointCloudReady: boolean,
  panelName: string = "Shadow Settings",
  initialConfig?: Partial<ShadowConfig>,
): ShadowSystemResult {
  const [shadowsReady, setShadowsReady] = useState(false);

  // 合并初始配置
  const effectiveConfig = useMemo(() => {
    return { ...DEFAULT_SHADOW_CONFIG, ...initialConfig };
  }, [initialConfig]);

  // 🎯 Leva控制面板配置 - 基于真实阴影效果
  const config = useControls(panelName, {
    enabled: {
      value: effectiveConfig.enabled,
    },
    initializationDelay: {
      value: effectiveConfig.initializationDelay,
      ...SHADOW_RANGES.initializationDelay,
    },
    "AccumulativeShadows 配置": {
      value: false,
      render: () => false, // 分组标题
    },
    frames: {
      value: effectiveConfig.accumulative.frames,
      ...SHADOW_RANGES.accumulative.frames,
    },
    color: {
      value: effectiveConfig.accumulative.color,
    },
    colorBlend: {
      value: effectiveConfig.accumulative.colorBlend,
      ...SHADOW_RANGES.accumulative.colorBlend,
    },
    toneMapped: {
      value: effectiveConfig.accumulative.toneMapped,
    },
    alphaTest: {
      value: effectiveConfig.accumulative.alphaTest,
      ...SHADOW_RANGES.accumulative.alphaTest,
    },
    opacity: {
      value: effectiveConfig.accumulative.opacity,
      ...SHADOW_RANGES.accumulative.opacity,
    },
    scale: {
      value: effectiveConfig.accumulative.scale,
      ...SHADOW_RANGES.accumulative.scale,
    },
    "RandomizedLight 配置": {
      value: false,
      render: () => false, // 分组标题
    },
    amount: {
      value: effectiveConfig.randomizedLight.amount,
      ...SHADOW_RANGES.randomizedLight.amount,
    },
    radius: {
      value: effectiveConfig.randomizedLight.radius,
      ...SHADOW_RANGES.randomizedLight.radius,
    },
    ambient: {
      value: effectiveConfig.randomizedLight.ambient,
      ...SHADOW_RANGES.randomizedLight.ambient,
    },
    bias: {
      value: effectiveConfig.randomizedLight.bias,
      ...SHADOW_RANGES.randomizedLight.bias,
    },
  });

  // 重置为默认值
  const resetToDefaults = useCallback(() => {
    console.log("🎯 重置阴影效果为默认配置");
    // Leva 的 set 函数会在下次渲染时自动重置，这里我们记录日志即可
  }, []);

  const resetShadows = useCallback(() => {
    console.log("🎯 智能阴影系统重置");
    setShadowsReady(false);

    // 短延迟后重新检查点云就绪状态
    const timer = setTimeout(() => {
      if (isPointCloudReady && config.enabled) {
        setShadowsReady(true);
        console.log("🎯 智能阴影系统重置完成");
      }
    }, config.initializationDelay || 100);

    return () => clearTimeout(timer);
  }, [isPointCloudReady, config.enabled, config.initializationDelay]);

  // 🎯 核心逻辑：基于点云就绪状态和配置启用状态控制阴影
  useEffect(() => {
    console.log("🎯 智能阴影系统状态检查:", { 
      isPointCloudReady, 
      enabled: config.enabled 
    });

    if (isPointCloudReady && config.enabled) {
      // 点云就绪且配置启用，可以安全启用阴影
      console.log("🎯 点云已就绪且阴影已启用，启用阴影系统");
      setShadowsReady(true);
    } else {
      // 点云未就绪或配置禁用，禁用阴影防止artifact
      console.log("🎯 点云未就绪或阴影已禁用，禁用阴影系统");
      setShadowsReady(false);
    }
  }, [isPointCloudReady, config.enabled]);

  return {
    config: {
      enabled: config.enabled,
      initializationDelay: config.initializationDelay,
      accumulative: {
        frames: config.frames,
        color: config.color,
        colorBlend: config.colorBlend,
        toneMapped: config.toneMapped,
        alphaTest: config.alphaTest,
        opacity: config.opacity,
        scale: config.scale,
      },
      randomizedLight: {
        amount: config.amount,
        radius: config.radius,
        ambient: config.ambient,
        bias: config.bias,
      },
    } as ShadowConfig,
    shadowsReady,
    resetShadows,
    resetToDefaults,
  };
}

// 类型导出，方便其他文件使用
export type { ShadowSystemResult };
